// ============================================
// TASK-9548 — grouped <select> options: pinned items get a LABEL, not just a spot
// ============================================
// Side-effect module (UMD): attaches `window.PickerGroups`. app.js imports it
// once, before any view module, and views read it through `window.PickerGroups`
// at CALL time — never a cross-`?v=` ES import (Pitfall #1). Node unit tests
// require() it directly: `node tests/task9548-picker-groups.unit.cjs`.
//
// WHY. TASK-1175 pinned internal-time customers (Shop / Office / yard) to the
// TOP of every clock customer picker so yard time stays one tap. Nothing said
// why, so a manager editing a clock entry looked for "Shop" under S, didn't
// find it, and gave up. A pinned item needs a visible reason: an <optgroup>
// label ("Internal — not billed") above it and "Customers" above the A–Z rest.
// Optgroup labels render on iOS Safari's native picker; option background
// colors do NOT, so this module never colors options.
//
// WHAT COUNTS AS INTERNAL HERE. `customer.internalTimeOnly === true` — the
// exact rule TASK-1175 pinned on, and the rule the clock-in write path uses to
// skip building a ticket (clock.js `isInternalTime`, dispatch.js / jobboard.js
// `skipInternalTime`). The payroll name fallback in internalTime.js (a customer
// NAMED "Shop" whose flag was never set) is deliberately NOT used: such a
// customer still gets a billing ticket at clock-in, so labelling it "not billed"
// would be false. Flag only.
//
// VALUES NEVER CHANGE. Every option keeps the value the old flat list gave it
// (the customer id) — only the order and the optgroup wrappers are new. When a
// list has no pinned group the output is a plain flat list (no lone
// "Customers" header for a tenant with no internal customers).
//
// ESCAPING. Labels and values are escaped here (&<>"'). `attrs` is a TRUSTED
// attribute string built by the caller (e.g. ` data-internal-time="1"`).
(function (root, factory) {
  const api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && typeof root === 'object') root.PickerGroups = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const INTERNAL_LABEL = 'Internal — not billed';
  const CUSTOMERS_LABEL = 'Customers';
  const HINT_MAX_NAMES = 3;

  function esc(s) {
    if (s === null || s === undefined) return '';
    return String(s).replace(/[&<>"']/g, m => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[m]);
  }

  function byLabel(a, b) {
    return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
  }

  function isInternalTimeCustomer(c) {
    return !!c && c.internalTimeOnly === true;
  }

  // TASK-10030 — a customer set Inactive (CRM saves 'Inactive'; older rows
  // and imports wrote 'inactive') or merged into another one. Case and
  // whitespace never matter: a lowercase-only check let every CRM-inactive
  // customer through the clock-in and Switch Job pickers.
  function isInactiveCustomer(c) {
    if (!c) return false;
    if (c.mergedIntoId) return true;
    return String(c.status || '').trim().toLowerCase() === 'inactive';
  }

  function defaultCustomerName(c) {
    return (c && (c.name || c.company)) || '';
  }

  // groups: [{ label, items: [{ value, label, selected?, attrs?, disabled? }] }]
  // Empty groups are dropped. A group with NO label (''/null) renders its items
  // flat, outside any <optgroup> — that is how a placeholder ("No specific
  // job", "All Yards") stays on top without a header over it (slice 2). When at
  // most one LABELED group survives, everything renders flat (no lone header)
  // unless opts.alwaysGroup.
  function groupedOptionsHtml(groups, opts) {
    const o = opts || {};
    const live = (groups || []).filter(g => g && Array.isArray(g.items) && g.items.length);
    const optHtml = it => {
      const sel = it.selected ? ' selected' : '';
      const dis = it.disabled ? ' disabled' : '';
      return `<option value="${esc(it.value)}"${it.attrs || ''}${sel}${dis}>${esc(it.label)}</option>`;
    };
    const labeled = live.filter(g => g.label);
    if (labeled.length <= 1 && !o.alwaysGroup) return live.map(g => g.items.map(optHtml).join('')).join('');
    return live.map(g => (g.label
      ? `<optgroup label="${esc(g.label)}">${g.items.map(optHtml).join('')}</optgroup>`
      : g.items.map(optHtml).join(''))).join('');
  }

  // Slice 2 — the job pickers (clock-in, Switch Job, Workforce edit/add/adjust,
  // JSA) all bucket a customer's jobs the same way (TASK-1082 relevance
  // scoring): dispatched today → date range covers today → most recent others.
  // The buckets used to be concatenated into one unlabeled list (or, on the JSA
  // form, split by a disabled "── Today ──" row). One label set for all of them.
  const JOB_GROUP_LABELS = Object.freeze({
    pinned: 'On this entry',  // admin edit: the entry's current job, even if out of window / finished
    today: 'Today',
    range: 'In progress',
    other: 'Recent',
  });

  // buckets: { pinned?, today, range, other } of job objects (already filtered
  // + capped by the caller). opts: placeholders [{value,label}] (flat, first),
  // selectedId, nameOf(j), optionAttrs(j) (TRUSTED attr string).
  function jobOptionsHtml(buckets, opts) {
    const o = opts || {};
    const b = buckets || {};
    const nameOf = o.nameOf || (j => (j && (j.title || j.name)) || 'Untitled Job');
    const sel = o.selectedId === undefined || o.selectedId === null ? null : String(o.selectedId);
    const toItem = j => ({
      value: j.id,
      label: nameOf(j),
      selected: sel !== null && sel !== '' && String(j.id) === sel,
      attrs: o.optionAttrs ? (o.optionAttrs(j) || '') : '',
    });
    const placeholders = (o.placeholders || []).map(p => ({
      value: p.value, label: p.label, selected: sel !== null && String(p.value) === sel && p.value !== '',
    }));
    return groupedOptionsHtml([
      { label: '', items: placeholders },
      { label: JOB_GROUP_LABELS.pinned, items: (b.pinned || []).map(toItem) },
      { label: JOB_GROUP_LABELS.today, items: (b.today || []).map(toItem) },
      { label: JOB_GROUP_LABELS.range, items: (b.range || []).map(toItem) },
      { label: JOB_GROUP_LABELS.other, items: (b.other || []).map(toItem) },
    ], { alwaysGroup: true });
  }

  // Split + sort a customer list. Returns { internal: [...], customers: [...] },
  // each A–Z by display name. Caller-side filtering (inactive etc.) happens
  // BEFORE this — this function never drops a customer.
  function splitCustomers(customers, opts) {
    const nameOf = (opts && opts.nameOf) || defaultCustomerName;
    const sorted = (customers || []).filter(Boolean).slice().sort((a, b) => byLabel(nameOf(a), nameOf(b)));
    return {
      internal: sorted.filter(isInternalTimeCustomer),
      customers: sorted.filter(c => !isInternalTimeCustomer(c)),
    };
  }

  // P0 2026-10-06 — vendors and subcontractors live in the same `customers`
  // collection (QuickBooks vendor import). The rule is window.BillableCustomer
  // (GENERATED from functions/src/shared/billableCustomer.js, imported by app.js
  // BEFORE this file), read at CALL time. Node tests set
  // globalThis.BillableCustomer = require(<canonical>).
  let _bcWarned = false;
  function billableOnly(customers, keepId) {
    const BC = typeof globalThis !== 'undefined' ? globalThis.BillableCustomer : null;
    if (BC && typeof BC.billableCustomers === 'function') return BC.billableCustomers(customers, keepId);
    if (!_bcWarned && typeof window !== 'undefined') {
      _bcWarned = true;
      console.error('[PickerGroups] window.BillableCustomer missing — customer pickers are NOT filtering vendors. Check the app.js import order.');
    }
    return (customers || []).filter(Boolean);
  }

  // The ONE customer-picker builder. opts:
  //   selectedId  — value to mark selected (string compare). A selected doc is
  //                 KEPT even when it is a vendor, so a record already pointing
  //                 at one still renders (never silently re-pointed on Save).
  //   nameOf(c)   — display name (default c.name || c.company || '')
  //   optionAttrs(c) — TRUSTED extra attribute string per option
  //   includeVendors — true ONLY for a vendor-facing picker (default false:
  //                 vendors/subcontractors are dropped, P0 2026-10-06)
  //   keepId      — id (or array of ids) ALSO kept through the vendor filter
  //                 without being marked selected (a picker that sets .value
  //                 after render). Always UNIONED with selectedId, so the
  //                 selected record can never be dropped (COD 2026-10-10).
  //   excludeInactive — true drops Inactive / merged customers (isInactiveCustomer);
  //                 selectedId / keepId still kept.
  //   excludeInternal — true for a BILLING / SALES picker (Invoice Builder,
  //                 Price Book, fuel surcharge override, rate sheet intake, CRM
  //                 opportunity, order link, public ticket builder, QuickBooks
  //                 mapping): internal-time customers (Shop / Office —
  //                 internalTimeOnly) are never billed, so they are not offered
  //                 (decision 2026-10-10). selectedId / keepId are still kept, so
  //                 a record that already points at one shows it — under the
  //                 "Internal — not billed" label — and is never re-pointed on
  //                 Save. Clock / workforce / job / project pickers do NOT pass
  //                 this: they keep the grouped Internal section.
  function customerOptionsHtml(customers, opts) {
    const o = opts || {};
    const nameOf = o.nameOf || defaultCustomerName;
    const sel = o.selectedId === undefined || o.selectedId === null ? null : String(o.selectedId);
    const keep = [sel].concat(o.keepId === undefined || o.keepId === null ? [] : (Array.isArray(o.keepId) ? o.keepId : [o.keepId]));
    if (!o.includeVendors) {
      customers = billableOnly(customers, keep);
    }
    if (o.excludeInternal) customers = withoutInternal(customers, keep);
    // TASK-10030 — excludeInactive: a NEW pick never offers an Inactive or
    // merged customer; selectedId / keepId are kept, so a record already on one
    // still shows it and is never re-pointed on Save.
    if (o.excludeInactive) {
      const keepSet = new Set(keep.filter(k => k !== null && k !== undefined && k !== '').map(String));
      customers = (customers || []).filter(c => c && (!isInactiveCustomer(c) || keepSet.has(String(c.id))));
    }
    const toItem = c => ({
      value: c.id,
      label: nameOf(c),
      selected: sel !== null && sel !== '' && String(c.id) === sel,
      attrs: o.optionAttrs ? (o.optionAttrs(c) || '') : '',
    });
    const parts = splitCustomers(customers, { nameOf });
    return groupedOptionsHtml([
      { label: INTERNAL_LABEL, items: parts.internal.map(toItem) },
      { label: CUSTOMERS_LABEL, items: parts.customers.map(toItem) },
    ], { alwaysGroup: parts.internal.length > 0 });
  }

  // The same excludeInternal rule for a caller that needs the LIST, not
  // <option> html (QuickBooks name matching + the manual mapping grid).
  // keepId (string | string[]) is kept even when internal.
  function withoutInternal(customers, keepId) {
    const keep = keepId === undefined || keepId === null ? [] : (Array.isArray(keepId) ? keepId : [keepId]);
    const keepSet = new Set(keep.filter(k => k !== null && k !== undefined && k !== '').map(String));
    return (customers || []).filter(c => c && (!isInternalTimeCustomer(c) || keepSet.has(String(c.id))));
  }

  // "Shop/Office = internal time, not billed" — names come from the tenant's
  // OWN internal customers (nothing here knows what any tenant calls them).
  // '' when the list has none, so no hint renders.
  function customerHintText(customers, opts) {
    const nameOf = (opts && opts.nameOf) || defaultCustomerName;
    const names = [];
    splitCustomers(customers, { nameOf }).internal.forEach(c => {
      const n = String(nameOf(c) || '').trim();
      if (n && !names.some(x => x.toLowerCase() === n.toLowerCase())) names.push(n);
    });
    if (!names.length) return '';
    const who = names.length <= HINT_MAX_NAMES ? names.join('/') : 'Internal customers';
    return `${who} = internal time, not billed`;
  }

  function customerHintHtml(customers, opts) {
    const text = customerHintText(customers, opts);
    if (!text) return '';
    const id = opts && opts.id ? ` id="${esc(opts.id)}"` : '';
    // opts.color — for modals that hardcode a light palette instead of theme
    // tokens (dispatch / jobboard driver sheets), so the hint can't inherit a
    // dark-theme text color onto a white sheet.
    const color = (opts && opts.color) || 'var(--text-secondary,#475569)';
    return `<div class="picker-group-hint"${id} data-picker-hint="internal" style="display:flex;align-items:center;gap:4px;margin-top:4px;font-size:0.75rem;line-height:1.3;color:${esc(color)};">`
      + `<span class="material-icons" aria-hidden="true" style="font-size:14px;">info</span><span>${esc(text)}</span></div>`;
  }

  return {
    INTERNAL_LABEL,
    CUSTOMERS_LABEL,
    JOB_GROUP_LABELS,
    esc,
    byLabel,
    isInternalTimeCustomer,
    isInactiveCustomer,
    groupedOptionsHtml,
    jobOptionsHtml,
    splitCustomers,
    customerOptionsHtml,
    withoutInternal,
    customerHintText,
    customerHintHtml,
  };
});
